import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CaretRightFilled } from '@ant-design/icons/lib';
import { Tabs } from 'antd';
import styled from 'styled-components';

import { OWNERSHIP_TYPES } from 'constants/ownership';
import {
  canViewShopperCommunication,
  isMysteryShopAdmin,
  isMysteryShopShopper,
} from 'helpers/permissions';
import { raiseUnderscore } from 'utils/helpers/string';

import Activities from './activities/Activities';
import Amenities from './amenities/Amenities';
import CareTypes from './careTypes/CareTypes';
import { useCommunityInfoContext } from './CommunityInfoContext';
import { COMMUNITY_INFO_TABS } from './constants';
import General from './general/General';
import CommunityIncentives from './incentives/CommunityIncentives';
import { canShowCommunityInfoPricingData } from './utils';
import { useAppSelector } from '../../store/hook';

const Label = styled.span`
  font-family: var(--font-regular);
  color: var(--text-secondary);
  font-size: 14px;
  text-shadow: none;
`;

const StyledTabs = styled(Tabs)`
  & {
    .ant-tabs-tab-active {
      ${Label} {
        font-family: var(--font-bold);
        color: var(--text-primary);
      }
    }
  }
`;

const getTabDataTestId = (tabKey: string) => `cd-community-info-tab-${raiseUnderscore(tabKey).toLowerCase()}`;

const CommunityInfoContent = () => {
  const { currentUser, selectedCommunity } = useAppSelector((state) => ({
    currentUser: state.auth.currentUser,
    selectedCommunity: state.global.selectedCommunity,
  }));
  const { mappedCareData, communityInfo, isCompetitor, mode } = useCommunityInfoContext();
  const showPricingData = canShowCommunityInfoPricingData(currentUser, mode);
  const location = useLocation();

  // Parse query parameter "tab"
  const queryParams = new URLSearchParams(location.search);
  const tabFromQuery = queryParams.get('tab');

  const [activeTab, setActiveTab] = useState(tabFromQuery || COMMUNITY_INFO_TABS.GENERAL);

  const careTypesTabs = mappedCareData
    .filter((careType) => careType.communityFees.length || careType.fees.length || careType.livingInfo.length)
    .map((tab: any) => ({
      label: <Label data-test-id={getTabDataTestId(tab.type)}>{tab.type}</Label>,
      key: tab.type,
      children: <CareTypes careType={tab} />,
    }));

  const tabs = [
    {
      label: <Label data-test-id={getTabDataTestId(COMMUNITY_INFO_TABS.GENERAL)}>General</Label>,
      key: COMMUNITY_INFO_TABS.GENERAL,
      children: <General />,
    },
    {
      label: <Label data-test-id={getTabDataTestId(COMMUNITY_INFO_TABS.AMENITIES)}>Amenities</Label>,
      key: COMMUNITY_INFO_TABS.AMENITIES,
      children: <Amenities />,
    },
  ];
  if (showPricingData) {
    tabs.push(...careTypesTabs);
  }

  const canViewActivitiesTab =
    !isCompetitor &&
    canViewShopperCommunication(currentUser) &&
    Boolean(currentUser?.isSuperuser || isMysteryShopAdmin(currentUser) || isMysteryShopShopper(currentUser));

  if (showPricingData && canViewActivitiesTab) {
    tabs.push({
      label: <Label>Communication</Label>,
      key: COMMUNITY_INFO_TABS.ACTIVITIES,
      children: <Activities />,
    });
  }

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(COMMUNITY_INFO_TABS.GENERAL);
    }
  }, [location.pathname, tabs]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const tabButtons = document.querySelectorAll<HTMLElement>('.ant-tabs-tab-btn[id]');
    const tabIdRegex = /-tab-(.+)$/;
    tabButtons.forEach((button) => {
      const match = tabIdRegex.exec(button.id);
      if (!match) {
        return;
      }

      const tabKey = match[1];
      const tabDataTestId = getTabDataTestId(tabKey);
      button.dataset.testId = tabDataTestId;
      const tabWrapper = button.closest<HTMLElement>('.ant-tabs-tab');
      if (tabWrapper) {
        tabWrapper.dataset.testId = `${tabDataTestId}-wrapper`;
      }
    });
  }, [activeTab, tabs]);

  if (showPricingData) {
    // For System Community Page create shared Incentives
    tabs.push({
      label: <Label data-test-id={getTabDataTestId(COMMUNITY_INFO_TABS.INCENTIVES)}>Incentives</Label>,
      key: COMMUNITY_INFO_TABS.INCENTIVES,
      children: (
        <CommunityIncentives
          communityInfo={communityInfo}
          ownershipType={selectedCommunity ? OWNERSHIP_TYPES.PRIVATE : OWNERSHIP_TYPES.SHARED}
        />
      ),
    });
  }

  return (
    <StyledTabs
      data-test-id="cd-community-info-tabs"
      moreIcon={<CaretRightFilled />}
      activeKey={activeTab}
      onChange={setActiveTab}
      tabPosition="top"
      tabBarGutter={30}
      size="small"
      destroyInactiveTabPane
      items={tabs}
    />
  );
};

export default CommunityInfoContent;
